import { computed, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { CardStack, CardStackSummary } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import findMany from "cc-frontend/helpers/find-many"
import type { PromiseObject } from "cc-frontend/lib/promise-object"
import type EmberStore from "cc-frontend/services/ember-store"
import { extractCardStackTemplateIds } from "cc-frontend/utils/filter-dates/course"
import { compact, concat, flatMap, isEqual, isNil, map, reject, uniq } from "lodash-es"
import { all } from "rsvp"
import styles from "./list.module.css"

interface Args {
  planbook: Planbook
  courses: PromiseObject<Array<Course>>
  chooseTemplate: Function
  createAndAssignTemplate: Function
  createTemplate: Function
}

export default class PlanbookTemplatesByNameListComponent extends Component<Args> {
  readonly styles = styles

  @service("ember-store") store!: EmberStore
  @findMany("templateIds", "card-stack-summary") usedTemplates!: PromiseObject<
    Array<CardStackSummary>
  >
  // Must use a CP to watch and compare courses.length _and_ planbook.relationships.cardStackTemplates.data.length length to make sure DOM updates upon deleting a template from the template library
  @computed(
    "args.courses.content.length",
    "args.planbook.relationships.cardStackTemplates.data.length"
  )
  get templateIds() {
    if (isNil(this.args.planbook?.relationships?.cardStackTemplates)) return []
    let planbookTemplateIds = map(
      get(this.args.planbook.relationships.cardStackTemplates, "data"),
      (datum) => get(datum, "id")
    )
    let courseTemplateIds = uniq(
      compact(
        flatMap(get(this.args.courses, "content"), (course) => extractCardStackTemplateIds(course))
      )
    )
    return reject(uniq(concat(planbookTemplateIds, courseTemplateIds)), (id) => id === "")
  }

  @tracked _fetchedCardStacks: Array<CardStack> = []

  lastTemplateIds: Array<string> = []

  fetchTemplates() {
    if (this.isDestroyed || this.isDestroying) return
    // Check if they're the same as last time so we don'd do a circular
    // loop as this will refire when we set this._fetchedCardStacks
    if (!isEqual(this.templateIds.sort(), this.lastTemplateIds.sort())) {
      this.lastTemplateIds = this.templateIds
      return all(map(this.templateIds, (id) => this.store.find("card-stack-summary", id))).then(
        (cardStacks) => (this._fetchedCardStacks = cardStacks)
      )
    }
    return null
  }

  get allTemplates(): Array<CardStack> {
    let promise = this.fetchTemplates()
    return this._fetchedCardStacks
  }
}
